import React, {memo, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch} from '~/redux/root-store';
import {useInput, useNotification} from '~/hooks';
import {
  Col,
  UiImage,
  UiText,
  UiQuestionnaireLayout,
  UiBox,
  UiSimpleGrid,
  UiButton,
  UiTextarea,
  UiFormErrorMessage,
  UiFormControl,
} from '~/components/elements';
import QuestionForm from '~/assets/images/icons/question-form.png';
import {RootState} from '~/redux/root-reducer';
import {EMERGENCY_CONTRACEPTION_KEYS} from '~/constants/medical-questionnaires';
import {Option} from '~/redux/questionnaires/types';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {spacing} from '~/utils/theme/spacing';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';

const Questionnaire11Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage, addValidationMessage} = useNotification(dispatch);

  const currentAnswer = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectData[EMERGENCY_CONTRACEPTION_KEYS.MEDICATION_BEING_TREATED]?.data,
  );

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === EMERGENCY_CONTRACEPTION_KEYS.MEDICATION_BEING_TREATED);

  let medicalOptions: Option[] = [];
  if (questionnaire?.medicationOptions?.length) {
    medicalOptions = questionnaire?.medicationOptions?.filter(i => i?.value !== 'NONE');
  }

  const rules = {
    medicationsTaking: (attributes: any) => {
      return {
        presence: {allowEmpty: false, message: `^This field can't be blank`},
        length: {
          minimum: 5,
          tooShort: '^This field is too short (minimum is %{count} characters)',
          tokenizer: function (value: any) {
            return trimSpaces(value);
          },
        },
      };
    },
  };

  const defaultOptions = medicalOptions.reduce<Record<string, boolean>>((res, cur) => {
    res[cur.value] = false;
    if (currentAnswer?.selectedMedication?.length) {
      currentAnswer?.selectedMedication.map((i: string) => {
        if (i !== 'NONE') res[i] = true;
      });
    }
    return res;
  }, {});

  const resetOptions = medicalOptions.reduce<Record<string, boolean>>((res, cur) => {
    res[cur.value] = false;

    return res;
  }, {});

  const {inputs, setInputs, validation, trimSpacesOnChange, trimSpaces} = useInput(
    {
      medicationsTaking: currentAnswer?.medicationsTaking || '',
      selectedOption: currentAnswer?.selectedOption || '',
      defaultOptions,
      noneOptions:
        (currentAnswer?.selectedMedication?.length && currentAnswer?.selectedMedication[0] === 'NONE') || false,
    },
    {rules},
  );

  const dataSubmit = Object.keys(inputs.defaultOptions).filter(i => inputs.defaultOptions[i] === true);

  const onClickOption = (answer: {value: string | number; label: string}) => {
    const {value} = answer;
    setInputs({noneOptions: false, defaultOptions: {...inputs.defaultOptions, [value]: !inputs.defaultOptions[value]}});
  };

  const onClickSubmit = async () => {
    if (inputs.selectedOption === 'YES') {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }
    dispatch(
      setQuestionnaireAnswer({
        key: EMERGENCY_CONTRACEPTION_KEYS.MEDICATION_BEING_TREATED,
        index: questionnaire?.index || '',
        data: {
          selectedMedication: inputs?.noneOptions ? ['NONE'] : dataSubmit,
          selectedOption: inputs.selectedOption,
          medicationsTaking: inputs.medicationsTaking,
        },
      }),
    );
    dispatch(setNextQuestionnaire({value: EMERGENCY_CONTRACEPTION_KEYS.NEGATIVE_BIRTH_CONTROL}));
  };

  const renderQnsMedication = () => {
    if (inputs.selectedOption === 'YES') {
      return (
        <UiBox mt={spacing.small} className="animate__animated animate__fadeInLeft">
          <UiText fontSize="md" fontWeight="bold">
            {questionnaire?.questions?.sub1}
          </UiText>
          <UiFormControl id="medicationsTaking" isRequired isInvalid={!!validation?.errors?.medicationsTaking}>
            <UiTextarea
              mt={spacing.small}
              bg="white"
              borderRadius={spacing.tiny}
              placeholder="Type your response here..."
              value={inputs.medicationsTaking}
              onChange={({target}) => setInputs({medicationsTaking: target.value})}
            />
            {validation?.errors?.medicationsTaking && (
              <UiFormErrorMessage>{validation?.errors?.medicationsTaking[0]}</UiFormErrorMessage>
            )}
          </UiFormControl>
        </UiBox>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      handleOnNext={onClickSubmit}
      actions={questionnaire?.actions}
      disableNext={(!dataSubmit.length && !inputs.noneOptions) || !inputs.selectedOption}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />

        <UiText fontSize="xl" fontWeight="bold" mt={spacing.large}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox mt={spacing.small} w="100%">
          <UiSimpleGrid columns={2} spacing={5}>
            {medicalOptions?.map(value => (
              <UiButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.defaultOptions[value.value] ? 'solid' : 'toggle'}
                onClick={() => onClickOption(value)}
              />
            ))}
          </UiSimpleGrid>
          <UiBox mt={spacing.small} display="flex" alignItems="center" justifyContent="center">
            <UiButton
              px={spacing.medium}
              title="None of the above"
              variant={inputs.noneOptions ? 'solid' : 'toggle'}
              onClick={() => setInputs({noneOptions: !inputs.noneOptions, defaultOptions: resetOptions})}
            />
          </UiBox>
        </UiBox>

        <UiText fontSize="md" fontWeight="bold" mt={spacing.small}>
          How about other medications?
        </UiText>
        <UiBox mt={spacing.small} w="100%">
          <UiSimpleGrid columns={2} spacing={5}>
            {questionnaire?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
                onClick={() => setInputs({selectedOption: value.value})}
              />
            ))}
          </UiSimpleGrid>
          {renderQnsMedication()}
        </UiBox>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire11Screen;
